<template>
  <img
    class="main_layout_bg sm-hide in_active"
    :src="`/images/games/${currentGameSlug}/banner_bg.webp`"
    loading="eager"
    alt="Boostingmarket.com website background image"
  />
  <Meta name="og:image" :content="`https://boostingmarket.com/images/og-images/${currentGameSlug}-og.png?v=13`" />
  <Meta name="twitter:image" :content="`https://boostingmarket.com/images/og-images/${currentGameSlug}-og.png?v=13`" />

  <div class="d-flex-column justify-start align-start gap-24 mt-20">
    <ElearningPackBanner
      :pack-title="elearingData.title"
      :pack-description="elearingData.description"
    />

    <div class="d-flex-row align-start space-between lg-row-to-col gap-24">
      <div
        class="d-flex-column flex-8 justify-start align-start t-white gap-24"
      >
        <div class="e_learning_cards_container">
          <ElearningCard
            v-for="price in elearingData?.prices"
            :key="price"
            :image="price.icon"
            :title="price.title"
            :price="price.price"
            :id="price.id"
            :on-click="clickCard"
            :slab="price.slab"
            :description="price.description"
            :tooltip-text="price.tooltip"
            :data="price"
          />
        </div>
        <RangePicker
          type="fort-hours-learning"
          :title="$t('calculators_common.number_of_hours')"
          :description="$t('calculators_common.select_number_of_hours')"
          v-if="checkoutStore.getDetails?.slab === 1"
        />
        <RangePicker
          type="fort-vod-learning"
          :title="$t('calculators_common.vod_reviews')"
          :description="$t('calculators_common.select_vod_reviews')"
          v-if="checkoutStore.getDetails?.slab === 2"
        />
        <RangePicker
          type="fort-games-learning"
          :title="$t('calculators_common.number_of_games')"
          :description="$t('calculators_common.select_number_of_games')"
          v-if="checkoutStore.getDetails?.slab === 3"
        />
        <PlatformPicker />

        <div
          class="d-flex-column justify-start xl-hide"
          v-if="elearingData?.faqs?.length > 0"
        >
          <p
            class="t-orange regular-l f-w-500 font-family-1"
            style="letter-spacing: 2px"
          >
            {{ $t("homepage.faq") }}s
          </p>
          <Faq :faqs="elearingData?.faqs" variant="pack" />
        </div>
      </div>

      <div
        class="d-flex-column flex-2 justify-start align-start t-white cart_container"
      >
        <cart />
        <SafeSecurePayments />
        <div
          class="d-flex-column justify-start above-xl-hide"
          v-if="elearingData?.faqs?.length > 0"
          style="margin-top: 24px"
        >
          <p
            class="t-orange regular-l f-w-500 font-family-1"
            style="letter-spacing: 2px"
          >
            {{ $t("homepage.faq") }}s
          </p>
          <Faq :faqs="elearingData?.faqs" variant="pack" />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import ElearningPackBanner from "~/components/banners/ElearningPackBanner.vue";
import PlatformPicker from "~/components/calculators/commonpickers/PlatformPicker.vue";
import RangePicker from "~/components/calculators/commonpickers/RangePicker.vue";
import ElearningCard from "~/components/cards/ElearningCard.vue";
import Cart from "~/components/calculators/checkout/Cart.vue";
import SafeSecurePayments from "~/components/calculators/checkout/SafeSecurePayments.vue";
import Faq from "~/components/custom/Faq.vue";
import { ref, onMounted, watch } from "vue";

definePageMeta({
  layout: "default",
  pageInfo: {
    name: "e-learning-game-page",
  },
  middleware: ["load-game"],
});

// HOOKS
const { currentGameSlug } = useGameComposable();
const { getLocale } = useFunctions();
const checkoutStore = useCheckoutStore();
const elearingData = ref({});
const { getGame, apiGetGame } = useGameStore();
const router = useRouter();
const route = router.currentRoute;
const localePath = useLocalePath();

apiGetGame({
  gameSlug: currentGameSlug,
  locale: getLocale(),
});

const { data: response, error } = await useApiFetch(
  `/api/get-elearning/${currentGameSlug}`,
  {
    method: "POST",
    fetchKey: `/api/get-elearning/${currentGameSlug}`,
    body: {
      locale: getLocale(),
      params: ["prices", "faqs"],
    },
  }
);
if (error.value?.data) {
  throw createError({
    statusCode: "404",
    statusMessage: "Not Found",
  });
}
// console.log(response.value);
if (response.value?.data) {
  elearingData.value = response.value?.data;
  useSeoMeta({
    title: elearingData.value?.meta_title,
    description: elearingData.value?.description,
  });

  checkoutStore.clearCheckout();

  checkoutStore.setPlatform({
    platform: 1,
  });
  checkoutStore.setDetails({
    numberOfHours: 1,
    slab: 1,
    elearningTitle: "Coaching",
  });

  if (getGame.id) {
    checkoutStore.setGameAndPack({
      game: getGame.id,
      pack: elearingData.value.id,
      fullGame: getGame,
      fullPack: elearingData.value,
    });
  }
}

const clickCard = () => {
  if (checkoutStore.getDetails.slab === 1) {
    checkoutStore.setDetails({
      ...checkoutStore.getDetails,
      numberOfHours: 1,
    });
  }
  if (checkoutStore.getDetails.slab === 2) {
    checkoutStore.setDetails({
      ...checkoutStore.getDetails,
      vodReviews: 1,
    });
  }
  if (checkoutStore.getDetails.slab === 3) {
    checkoutStore.setDetails({
      ...checkoutStore.getDetails,
      numberOfGames: 1,
    });
  }
};

onMounted(() => {
  checkoutStore.setCalculating();
});
addCanonicalLink(route.value.fullPath);

onNuxtReady(() => {
  if (elearingData.value) {
    analytics.page("Game Pack Page", {
      title: elearingData.value.title,
    });
  }
});

watch(
  () => getGame,
  (newVal) => {
    checkoutStore.setGameAndPack({
      game: newVal.id,
      pack: elearingData.value.id,
      fullGame: newVal,
      fullPack: elearingData.value,
    });
  }
);
</script>
